import { useState, useSyncExternalStore } from 'react';
import { ChevronDown, ChevronRight, type LucideIcon } from '../../icons';
import { tShared, getSharedComponentLang, subscribeSharedComponentLang } from '../../lib/shared-i18n';
import './SidebarGroup.css';

/**
 * SidebarGroup — 시안 InfoView (line 2935~) 의 "group header + sub-items 토글" 패턴 generic 추출.
 *
 * 재사용 페이지:
 *   - BoardPage (알림사항): 4 group (공지/게시판/문의/FAQ)
 *
 * 정책:
 *   - sub-item 의 `disabled: true` 는 학습 `pattern-disabled-link-placeholder-when-target-page-deferred`
 *     에 따라 click 무반응 + "준비 중" tooltip + opacity ↓.
 *   - active item 은 `aria-current="page"` 로 스크린리더 안내.
 *   - group toggle 은 `defaultExpanded` (uncontrolled) 또는 props 로 controlled 가능.
 */

export interface SidebarSubItem {
  id: string;
  label: string;
  icon: LucideIcon;
  badge?: number;
  disabled?: boolean;
  /**
   * 항목 아래 한 줄 — **「이 화면이 무엇을 정하는가」** (2026-08-31 · 시안 F1).
   * ★ 근태·인사 트리는 이미 부제를 단다. 결재 관리 항목만 이름뿐이라, 「양식 · 서식」이
   *   무엇을 다루는지(종류 · 칸 · 전결) 눌러 봐야 알았다.
   * ★ 안 주면 종전 그대로 한 줄이다 — 다른 트리(게시판)는 안 바뀐다.
   */
  hint?: string;
}

export interface SidebarGroupProps {
  groupKey: string;
  label: string;
  icon: LucideIcon;
  items: ReadonlyArray<SidebarSubItem>;
  activeId: string | null;
  onSelect: (id: string) => void;
  defaultExpanded?: boolean;
  expanded?: boolean; // controlled (제공 시 onToggle 도 제공)
  onToggle?: (next: boolean) => void;
}

export function SidebarGroup({
  groupKey,
  label,
  icon: GroupIcon,
  items,
  activeId,
  onSelect,
  defaultExpanded = true,
  expanded,
  onToggle,
}: SidebarGroupProps) {
  useSyncExternalStore(subscribeSharedComponentLang, getSharedComponentLang, getSharedComponentLang);
  const [internalOpen, setInternalOpen] = useState(defaultExpanded);
  const isOpen = expanded ?? internalOpen;

  const handleToggle = () => {
    const next = !isOpen;
    if (onToggle) onToggle(next);
    else setInternalOpen(next);
  };

  return (
    <div className="sidebar-group" data-group-key={groupKey}>
      <button
        type="button"
        className="sidebar-group__header"
        onClick={handleToggle}
        aria-expanded={isOpen}
      >
        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <GroupIcon size={15} className="sidebar-group__group-icon" />
        <span className="sidebar-group__label">{label}</span>
      </button>

      {isOpen && (
        <ul className="sidebar-group__items" role="list">
          {items.map((item) => {
            const ItemIcon = item.icon;
            const isActive = activeId === item.id;
            const isDisabled = item.disabled === true;
            return (
              <li key={item.id}>
                <button
                  type="button"
                  className={`sidebar-group__item ${isActive ? 'is-active' : ''} ${
                    isDisabled ? 'is-disabled' : ''
                  }`}
                  onClick={() => {
                    if (!isDisabled) onSelect(item.id);
                  }}
                  disabled={isDisabled}
                  aria-current={isActive ? 'page' : undefined}
                  title={isDisabled ? tShared('shared.sidebar_group.pending') : undefined}
                >
                  <ItemIcon size={14} className="sidebar-group__item-icon" />
                  <span className="sidebar-group__item-label">
                    {item.label}
                    {item.hint && <span className="sidebar-group__item-hint">{item.hint}</span>}
                  </span>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className="sidebar-group__item-badge">{item.badge}</span>
                  )}
                  {isDisabled && (
                    <span className="sidebar-group__item-pending">{tShared('shared.sidebar_group.pending')}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
